import { isNaN, isNull, isUndefined } from 'lodash';
import { ReactNode, CSSProperties } from 'react';
import { MIN_REVENUE_IN_USD } from '../../../../common/model/input/companies/data-points/revenues/consts';
import { NetScoreFormat } from '../../../../common/model/output/scores/types';
import { Valence } from '../../../../common/model/types';
import { plusIfPositive } from '../../upright/model/output/scores/net-impact-chart/shared';
import { BouColor } from '../global/bou-colors';

export function NA() {
  return <span style={{ color: BouColor.TOOL_TEXT_DISABLED_GRAY }}>N/A</span>;
}

export function formatRevenueWithColor(valueUsd: number | null | undefined) {
  if (valueUsd === undefined || valueUsd === null) {
    return <span style={{ color: '#C0C0C0' }}>N/A</span>;
  }
  if (valueUsd < MIN_REVENUE_IN_USD) {
    valueUsd = MIN_REVENUE_IN_USD;
  }
  return Math.round(valueUsd).toLocaleString(); // TODO: locale-independent format
}

export function formatMarketSizeWithColor(value: number | string | undefined | null) {
  if (value === undefined || value === null) {
    return <span style={{ color: '#C0C0C0' }}>N/A</span>;
  }
  return formatMarketSize(value);
}

export function formatMarketSize(value: number | string | undefined | null) {
  if (value === undefined || value === null) {
    return 'N/A';
  }
  return Math.round(+value).toLocaleString(); // TODO: locale-independent format
}

export function formatRevenue(value: number | string | undefined | null) {
  if (value === undefined || value === null) {
    return 'N/A';
  }
  return Math.round(+value).toLocaleString(); // TODO: locale-independent format
}

export function formatIncome(value: number | string | undefined | null) {
  if (value === undefined || value === null) {
    return 'N/A';
  }
  return Math.round(+value).toLocaleString(); // TODO: locale-independent format
}

export function formatEmployeeCount(value?: number) {
  return formatCount(value);
}

export const nbsp = '\xa0';

export function formatRatioAsPercentage(
  value?: number | null,
  decimals = 1,
  undefinedAsZero = false,
  includeUnit = true,
  showAsterisk = false,
) {
  if ((value === undefined || value === null) && !undefinedAsZero) {
    return 'N/A';
  }
  return `${((value || 0) * 100).toFixed(decimals)}${showAsterisk ? '*' : ''}${includeUnit ? `${nbsp}%` : ''}`;
}

export function formatCount(value?: number | null) {
  if (value === undefined || value === null) {
    return 'N/A';
  }
  return Math.round(value).toLocaleString(); // TODO: locale-independent format
}

export function formatCountWithZeroForNil(value?: number | null) {
  if (value === undefined || value === null) {
    return '0';
  }
  return Math.round(value).toLocaleString(); // TODO: locale-independent format
}

export function formatMatchCountWithColor(value?: number | null) {
  if (value === undefined || value === null) {
    return <span style={{ color: BouColor.TOOL_TEXT_DISABLED_GRAY }}>N/A</span>;
  }
  return Math.round(value).toLocaleString();
}

export function formatWithTwoDecimalsGrayNA(value?: number | null) {
  if (value === undefined || value === null) {
    return <span style={{ color: BouColor.TOOL_TEXT_DISABLED_GRAY }}>N/A</span>;
  }
  return value.toFixed(2);
}

export function toPercentageValue(value?: number, decimals?: number) {
  if (value === undefined) {
    return 'N/A';
  }
  return `${(value * 100).toFixed(decimals || 0)}`;
}

export function roundToTwoDecimals(value: number): number {
  return Math.round(value * 100) / 100;
}

export function roundToTwoDecimalsPercentage(value: number | null | undefined) {
  if (!value) return value;
  return Math.round(value * 100 * 100) / 100;
}

export function formatPercentage(value?: number, decimals?: number) {
  return `${toPercentageValue(value, decimals ?? 2)} %`;
}

export function formatNetImpactRatio(value?: number | null, addPlusIfPositive?: boolean, decimals?: number): ReactNode {
  if (value === undefined || value === null || isNaN(value)) {
    return <span style={{ color: BouColor.TOOL_TEXT_DISABLED_GRAY }}>N/A</span>;
  }
  return formatNetImpactRatioString(value, addPlusIfPositive, decimals);
}

export function formatNetImpactRatioString(value?: number, addPlusIfPositive?: boolean, decimals?: number): string {
  if (value === undefined || isNaN(value)) {
    return 'N/A';
  }
  return `${addPlusIfPositive ? plusIfPositive(value) : ''}${toPercentageValue(value, decimals)}%`;
}

export function formatNetScore(score: number, format: NetScoreFormat): string {
  return format === NetScoreFormat.NET_RATIO
    ? `${formatNetImpactRatioString(score, true)}`
    : `${plusIfPositive(score)}${score.toFixed(1)}`;
}

export function formatNetScoreGrayNA(score: number | undefined | null, format: NetScoreFormat) {
  if (isUndefined(score) || isNull(score)) {
    return <NA />;
  }
  return format === NetScoreFormat.NET_RATIO
    ? `${formatNetImpactRatioString(score, true)}`
    : `${plusIfPositive(score)}${score.toFixed(1)}`;
}

export function formatPercentageGrayNA(value?: number | null) {
  if (value === undefined || value === null) {
    return <span style={{ color: BouColor.TOOL_TEXT_DISABLED_GRAY }}>N/A</span>;
  }
  return `${(value * 100).toFixed(2)} %`;
}

export function formatPercentageWithColor(value?: number | null, decimals = 2, undefinedAsZero = false) {
  if (!undefinedAsZero && (value === undefined || value === null)) {
    return <span style={{ color: BouColor.TOOL_TEXT_DISABLED_GRAY }}>N/A</span>;
  }
  return `${((value || 0) * 100).toFixed(decimals)} %`;
}

export function formatAbsoluteScoreGrayNA(value?: number | null) {
  if (value === undefined || value === null) {
    return <span style={{ color: BouColor.TOOL_TEXT_DISABLED_GRAY }}>N/A</span>;
  }
  const digits = Math.abs(value) < 50 ? 1 : 0;
  return value.toFixed(digits);
}

export function formatAbsoluteScoreForNetImpactChart(value?: number | null) {
  if (value === undefined || value === null) {
    return 'N/A';
  }
  return Math.round(value).toLocaleString(); // TODO: locale-independent format
}

export function scoreTextColor(value: number, negativeColor?: string, positiveColor?: string) {
  if (value < 0) {
    return negativeColor || BouColor.IMPACT_NEGATIVE_PURPLE;
  } else if (value > 0) {
    return positiveColor || BouColor.IMPACT_POSITIVE_GREEN;
  }
  return BouColor.BLACK;
}

// *TODO: DELETE after new design is ready
export function scoreBackroundColor(value: number | undefined) {
  if (value && value < 0) {
    return valenceBackgroundColor(Valence.NEGATIVE);
  } else if (value && value > 0) {
    return valenceBackgroundColor(Valence.POSITIVE);
  }
  return valenceBackgroundColor(Valence.POSITIVE); // TODO
}

export function valenceBackgroundColor(valence: Valence): string {
  switch (valence) {
    case Valence.POSITIVE:
      return '#B8F3C4';
    case Valence.NEGATIVE:
      return '#FFBDF1';
    case Valence.OTHER:
    default:
      return BouColor.TOOL_BACKGROUND_LIGHT_GRAY;
  }
}

export function valenceColor(valence: Valence): string {
  switch (valence) {
    case Valence.POSITIVE:
      return BouColor.IMPACT_POSITIVE_GREEN;
    case Valence.NEGATIVE:
      return BouColor.IMPACT_NEGATIVE_PURPLE;
    case Valence.OTHER:
    default:
      return BouColor.TOOL_BORDER_LIGHT_GRAY;
  }
}

export function valenceTextColorPrimer(valence: Valence): string {
  // Gets keys to use with Primer component's sx prop
  switch (valence) {
    case Valence.POSITIVE:
      return 'impact.positive.text.dark';
    case Valence.NEGATIVE:
      return 'impact.negative.text.dark';
    case Valence.OTHER:
    default:
      return 'text.muted';
  }
}

export function formatScoreColor(value: number | undefined, style?: CSSProperties) {
  const color = value !== undefined ? scoreBackroundColor(value) : 'gray';
  return (
    <div
      style={{
        backgroundColor: color,
        paddingTop: 4,
        paddingBottom: 4,
        width: 70,
        textAlign: 'center',
        ...style,
      }}
    >
      {value !== undefined ? value.toFixed(2) : 'N/A'}
    </div>
  );
}

export function formatScoreColorWithPlus(value: number | undefined, style?: CSSProperties) {
  const color = value !== undefined ? scoreBackroundColor(value) : 'lightgray';
  return (
    <div
      style={{
        backgroundColor: color,
        paddingTop: 4,
        paddingBottom: 4,
        width: 70,
        textAlign: 'center',
        ...style,
      }}
    >
      {value ? plusIfPositive(value) : undefined}
      {value !== undefined ? value.toFixed(2) : 'N/A'}
    </div>
  );
}
